import React, {createContext, useContext, useEffect, useState, ReactNode} from 'react';
import {User, Session} from '@supabase/supabase-js';
import getSupabaseClient from '../services/supabaseService';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithOAuth: (provider: 'google' | 'facebook') => Promise<void>;
  signOut: () => Promise<void>;
  isLoggedIn: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{children: ReactNode}> = ({children}) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const supabase = getSupabaseClient();

  useEffect(() => {
    // Vérifier la session actuelle et traiter le callback OAuth si présent
    const initAuth = async () => {
      // Vérifier s'il y a un code OAuth dans l'URL (callback OAuth)
      if (typeof window !== 'undefined') {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const searchParams = new URLSearchParams(window.location.search);
        
        // Si on a un code OAuth dans l'URL, Supabase va le traiter automatiquement avec detectSessionInUrl: true
        const hasOAuthParams = hashParams.has('access_token') || 
                               hashParams.has('code') || 
                               searchParams.has('code');
        
        if (hasOAuthParams) {
          console.log('[AuthContext] OAuth callback detected, waiting for session...');
        }
      }
      
      // Récupérer la session actuelle
      const {data: {session}, error} = await supabase.auth.getSession();
      
      if (error) {
        console.error('[AuthContext] Error getting session:', error);
      }
      
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      // Nettoyer l'URL après traitement du callback OAuth (sur web uniquement)
      if (typeof window !== 'undefined' && session) {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const searchParams = new URLSearchParams(window.location.search);
        
        if (hashParams.has('access_token') || hashParams.has('code') || searchParams.has('code')) {
          // Nettoyer l'URL en enlevant les paramètres OAuth
          const cleanUrl = window.location.origin + window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
          console.log('[AuthContext] OAuth callback processed, URL cleaned');
        }
      }
    };

    initAuth();

    // Écouter les changements d'authentification
    const {
      data: {subscription},
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[AuthContext] Auth state changed:', event, session?.user?.email);
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      // Nettoyer l'URL après un changement d'état OAuth (sur web uniquement)
      if (event === 'SIGNED_IN' && typeof window !== 'undefined' && session) {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const searchParams = new URLSearchParams(window.location.search);
        
        if (hashParams.has('access_token') || hashParams.has('code') || searchParams.has('code')) {
          const cleanUrl = window.location.origin + window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
          console.log('[AuthContext] OAuth callback processed after SIGNED_IN event, URL cleaned');
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [supabase]);

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    const {data, error} = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      setLoading(false);
      throw error;
    }

    setSession(data.session);
    setUser(data.user);
    setLoading(false);
  };

  const signInWithOAuth = async (provider: 'google' | 'facebook') => {
    setLoading(true);
    const {error} = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: typeof window !== 'undefined' ? window.location.origin : undefined,
      },
    });

    if (error) {
      setLoading(false);
      throw error;
    }
    // Note: Sur web, l'utilisateur sera redirigé vers le provider OAuth
    // Le callback OAuth gérera la session via onAuthStateChange après le redirect
    // Sur mobile, le loading sera géré par onAuthStateChange aussi
    // Ne pas mettre loading à false ici car on attend le redirect/callback
  };

  const signOut = async () => {
    setLoading(true);
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    setLoading(false);
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    signIn,
    signInWithOAuth,
    signOut,
    isLoggedIn: !!session,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
